((globalScope, factory) => {
  const api = factory();

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (globalScope) globalScope.BPMysteryBuilderCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, () => {
  const CATEGORIES = Object.freeze(['makeup', 'skincare', 'mixed']);
  const TIERS = Object.freeze(['beauty', 'luxury']);
  const SIZES = Object.freeze(['small', 'medium', 'large', 'premium']);

  const toInteger = (value) => {
    if (value === '' || value === null || typeof value === 'undefined') return null;
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
  };

  const interpolate = (template, replacements) =>
    Object.entries(replacements).reduce(
      (output, [key, value]) => output.split(`__${key}__`).join(String(value)),
      String(template || ''),
    );

  const formatQuantity = (minimum, maximum, labels = {}) => {
    const min = toInteger(minimum);
    const max = toInteger(maximum);
    if (!min || !max || max < min) return '';

    if (min === max) {
      const template = min === 1 ? labels.fixedOne : labels.fixedOther;
      return interpolate(template || (min === 1 ? '__count__ product' : '__count__ products'), {
        count: min,
      });
    }

    return interpolate(labels.range || 'From __min__ to __max__ products', {
      min,
      max,
    });
  };

  const getStepKeys = (category) =>
    category === 'skincare'
      ? Object.freeze(['category', 'tier', 'size'])
      : Object.freeze(['category', 'tier', 'size', 'complexion']);

  const validateVariantData = (variant, options = {}) => {
    if (!options.productConfigured) return { valid: false, code: 'missing_product' };
    if (!variant || !String(variant.id || '').trim()) return { valid: false, code: 'missing_variant' };

    const priceCents = toInteger(variant.priceCents);
    const itemCountMin = toInteger(variant.itemCountMin);
    const itemCountMax = toInteger(variant.itemCountMax);
    const minimumValueCents = toInteger(variant.minimumValueCents);
    const weightGrams = toInteger(variant.weightGrams);

    if (!priceCents || priceCents < 1) return { valid: false, code: 'missing_price' };
    if (!itemCountMin || itemCountMin < 1) return { valid: false, code: 'missing_min' };
    if (!itemCountMax || itemCountMax < 1) return { valid: false, code: 'missing_max' };
    if (itemCountMax < itemCountMin) return { valid: false, code: 'invalid_range' };
    if (!minimumValueCents || minimumValueCents < 1) {
      return { valid: false, code: 'missing_minimum_value' };
    }
    if (minimumValueCents <= priceCents) {
      return { valid: false, code: 'invalid_minimum_value' };
    }
    if (!weightGrams || weightGrams < 1) return { valid: false, code: 'missing_weight' };
    if (options.requireAvailable && !variant.available) return { valid: false, code: 'sold_out' };

    return {
      valid: true,
      code: null,
      normalized: {
        priceCents,
        itemCountMin,
        itemCountMax,
        minimumValueCents,
        weightGrams,
        countMode: itemCountMin === itemCountMax ? 'fixed' : 'range',
      },
    };
  };

  const sanitizeState = (candidate = {}) => {
    const category = CATEGORIES.includes(candidate.category) ? candidate.category : null;
    const tier = TIERS.includes(candidate.tier) ? candidate.tier : null;
    let size = SIZES.includes(candidate.size) ? candidate.size : null;
    if (tier === 'beauty' && size === 'premium') size = null;

    const allowedSkinTones = [
      'very-light',
      'light',
      'medium',
      'medium-deep',
      'deep',
      'very-deep',
      'unknown',
    ];
    const allowedUndertones = ['cool', 'neutral', 'warm', 'olive', 'unknown'];

    return {
      category,
      tier,
      size,
      skinTone: allowedSkinTones.includes(candidate.skinTone) ? candidate.skinTone : null,
      undertone: allowedUndertones.includes(candidate.undertone) ? candidate.undertone : null,
      baseFaceOptOut: Boolean(candidate.baseFaceOptOut),
    };
  };

  const buildLineProperties = (state, variant, labels, version, options = {}) => {
    const quantityLabel = formatQuantity(variant.itemCountMin, variant.itemCountMax, labels.quantity);
    const countMode = Number(variant.itemCountMin) === Number(variant.itemCountMax) ? 'fixed' : 'range';
    const properties = {
      [labels.publicQuantity]: quantityLabel,
      [labels.publicMinimumValue]: variant.minimumValueFormatted,
      '_Configuratore Mystery': options.configuratorLabel || 'BeautyPrice Next Mystery Builder',
      '_Versione configuratore': version,
      '_Categoria selezionata': state.category,
      '_Selezione scelta': state.tier,
      '_Dimensione scelta': variant.sizeLabel,
      '_Item count min': String(variant.itemCountMin),
      '_Item count max': String(variant.itemCountMax),
      '_Minimum value snapshot': variant.minimumValueFormatted,
      '_Count mode': countMode,
      '_Product key': variant.productKey,
      '_Variant key': variant.variantKey,
    };

    if (state.category !== 'skincare') {
      properties[labels.publicSkinTone] = labels.skinTones[state.skinTone] || '';
      properties[labels.publicUndertone] = labels.undertones[state.undertone] || '';
      properties[labels.publicBaseFace] = state.baseFaceOptOut
        ? labels.baseFaceExcluded
        : labels.baseFaceNoExclusion;
    }

    return Object.fromEntries(
      Object.entries(properties).filter(([, value]) => value !== '' && value !== null && value !== undefined),
    );
  };

  const buildAnalyticsPayload = (state, variant, extra = {}) => {
    const payload = {
      category: state?.category || undefined,
      tier: state?.tier || undefined,
      size: state?.size || undefined,
      count_mode: variant?.countMode || undefined,
      item_count_min: variant ? toInteger(variant.itemCountMin) : undefined,
      item_count_max: variant ? toInteger(variant.itemCountMax) : undefined,
      price: variant?.priceCents ? Number(variant.priceCents) / 100 : undefined,
      step: extra.step,
      error_code: extra.errorCode,
    };

    return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined && value !== null));
  };

  const createSubmissionGate = () => {
    let pending = false;

    return Object.freeze({
      get pending() {
        return pending;
      },
      async run(task) {
        if (pending) return { skipped: true };
        pending = true;
        try {
          return await task();
        } finally {
          pending = false;
        }
      },
    });
  };

  return Object.freeze({
    CATEGORIES,
    TIERS,
    SIZES,
    buildAnalyticsPayload,
    buildLineProperties,
    createSubmissionGate,
    formatQuantity,
    getStepKeys,
    sanitizeState,
    validateVariantData,
  });
});
